import { Input, UnarchiveOutlined } from "@mui/icons-material";
import { IconButton } from "@mui/material";
import { DataType } from "ka-table";
import { Column } from "ka-table/models";
import { useMemo } from "react";
import { Link } from "react-router-dom";
import CrmTable from "../../../App/Components/Table/CrmTable";
import { IArchivedGroup } from "../../../App/Typings/Groups";
import { ITablePagination } from "../../../App/Typings/SortableTable";
import { GroupChip } from "../DisplayGroupList/DisplayGroupList";

const columns: Column[] = [
  {
    title: "Name",
    key: "name",
    dataType: DataType.String,
  },
  { title: "Type", key: "type", dataType: DataType.String },
  { title: "Archived By", key: "username", dataType: DataType.Number, isSortable: false },
  {
    title: "Date Archived",
    key: "archivedDate",
    dataType: DataType.Date,
  },
  {
    title: "Actions",
    key: "actions",
    dataType: DataType.Object,
    isEditable: false,
    isSortable: false,
    isFilterable: false,
    width: 125,
  },
];

export default function ArchivedGroupsTable({
  pagination,
  isLoading,
  isUnarchiveLoading,
  isAllSelected,
  groups,
  onGroupUnarchive,
  onRowClick,
  onRowSelect,
  isRowSelected,
  onAllSelected,
  sort,
  sortBy,
  onSort,
}: {
  pagination: ITablePagination;
  isLoading: boolean;
  isUnarchiveLoading: boolean;
  isAllSelected: boolean;
  groups: IArchivedGroup[];
  onGroupUnarchive: (groupId: string) => void;
  onRowClick: (row: IArchivedGroup) => void;
  onRowSelect: (row: IArchivedGroup, selected: boolean) => void;
  isRowSelected: (row: IArchivedGroup) => boolean;
  onAllSelected: (selected: boolean) => void;
  sort: "asc" | "desc";
  sortBy: string;
  onSort: (prop: string) => void;
}) {
  const GetGroupActions = (group: IArchivedGroup) => {
    return (
      <>
        <UnarchiveGroup
          group={group}
          isLoading={isUnarchiveLoading}
          onClick={() => onGroupUnarchive(group.id)}
        />
        <ViewGroup group={group} />
      </>
    );
  };

  const groupData = useMemo(() => {
    return groups.map((c) => {
      return {
        ...c,
        type: <GroupChip group={c} />,
        actions: GetGroupActions(c),
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups]);

  return (
    <CrmTable
      columns={columns}
      data={groupData}
      pagination={pagination}
      loading={isLoading}
      name="archived-groups"
      onRowClick={onRowClick}
      rowTitle="Archived Group"
      onSort={onSort}
      order={sort}
      orderBy={sortBy}
      rowTestId={(row) => `archived-group-${row.id}`}
      isAllSelected={isAllSelected}
      rowSelection={{
        onRowSelect: (row: IArchivedGroup, _selected: boolean) => onRowSelect(row, !isRowSelected(row)),
        isRowSelected,
        onAllSelected,
      }}
    />
  );
}

const UnarchiveGroup = ({
  group,
  isLoading,
  onClick,
}: {
  group: IArchivedGroup;
  isLoading: boolean;
  onClick: () => void;
}) => (
  <IconButton
    title={`Unarchive ${group.name}`}
    data-test={`unarchive-group-${group.id}`}
    disabled={isLoading}
    onClick={(e) => {
      e.stopPropagation();
      onClick();
    }}
  >
    <UnarchiveOutlined fontSize="small" />
  </IconButton>
);

const ViewGroup = ({ group }: { group: IArchivedGroup }) => (
  <IconButton
    title={`View ${group.name} Profile`}
    data-test={`view-group-${group.id}`}
    component={Link}
    to={`/candidategroups/${group.id}`}
    onClick={(e) => e.stopPropagation()}
  >
    <Input fontSize="small" />
  </IconButton>
);
